import React from 'react';
import PropTypes from 'prop-types';
import { alpha, useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import PeopleIcon from '@material-ui/icons/People';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import LinkIcon from '@material-ui/icons/Link';
import MovieCreationIcon from '@material-ui/icons/MovieCreation';

const DEFAULT_TITLE = 'Lo que nos hace únicos';
const DEFAULT_DESCRIPTION =
  'Somos una red audiovisual y editorial con más de una década de trayectoria. Combinamos la fuerza del Live Streaming global con la permanencia del contenido On-Demand y la autoridad del periodismo digital en nuestro ecosistema.';

const BENEFIT_ITEMS = [
  {
    title: 'Ecosistema 360° | Live, On-Demand & Editorial',
    text: 'Más de una década conectando marcas con audiencias. Live streaming global, archivo On-Demand y periodismo digital en un solo ecosistema comercial.',
    accent: '#00838f',
    Icon: PeopleIcon,
  },
  {
    title: 'TNO Digest: autoridad en contenido escrito',
    text: 'Transformamos la conversación en información. Noticias, artículos de valor y tendencias con alta visibilidad y posicionamiento SEO para tu marca.',
    accent: '#c2185b',
    Icon: MenuBookIcon,
  },
  {
    title: 'El puente hacia tu audiencia',
    text: 'Canales bidireccionales donde tu marca no solo emite un mensaje: dialoga con consumidores reales en tiempo real y en redes.',
    accent: '#1565c0',
    Icon: LinkIcon,
  },
  {
    title: 'TNO Media Lab: tu partner de producción',
    text: 'Más que espacios publicitarios: asesoría integral y ejecución técnica desde la idea creativa hasta la pieza final con estándares internacionales.',
    accent: '#00acc1',
    Icon: MovieCreationIcon,
  },
];

function BenefitCard({ item }) {
  const { title, text, accent, Icon } = item;
  return (
    <Box
      sx={{
        height: '100%',
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        bgcolor: 'background.paper',
        border: `1px solid ${alpha(accent, 0.2)}`,
        borderTop: `4px solid ${accent}`,
        boxShadow: `0 10px 28px ${alpha(accent, 0.1)}`,
        background: `linear-gradient(180deg, ${alpha(accent, 0.05)} 0%, transparent 42%)`,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 16px 40px ${alpha(accent, 0.16)}`,
        },
      }}
    >
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.75,
          bgcolor: alpha(accent, 0.12),
          color: accent,
        }}
      >
        <Icon sx={{ fontSize: 28 }} />
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: '1.02rem', lineHeight: 1.3, mb: 1 }}>{title}</Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', lineHeight: 1.6 }}>{text}</Typography>
    </Box>
  );
}

BenefitCard.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    accent: PropTypes.string.isRequired,
    Icon: PropTypes.elementType.isRequired,
  }).isRequired,
};

const Benefits = ({ content = {} }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || '#00838f';
  const title = content.title || DEFAULT_TITLE;
  const description = content.description || DEFAULT_DESCRIPTION;

  return (
    <Box
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        background: `linear-gradient(165deg, ${alpha(secondary, 0.07)} 0%, ${alpha(primary, 0.05)} 50%, transparent 100%)`,
        px: { xs: 1, md: 2 },
        py: { xs: 3, md: 5 },
        my: { xs: 2, md: 3 },
      }}
    >
      <Box textAlign="center" mb={{ xs: 3, md: 4 }} maxWidth={760} mx="auto">
        <Typography
          sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 2.5, fontSize: '0.78rem' }}
          color="secondary"
          gutterBottom
        >
          {content.overline || 'Diferenciadores'}
        </Typography>
        <Typography
          component="h2"
          sx={{
            fontWeight: 900,
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            letterSpacing: '-0.02em',
            color: 'primary.main',
            mb: 1.5,
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '1.02rem', lineHeight: 1.7 }}>
          {description.includes('tnonetwork.com') ? (
            <>
              {description.split(/(www\.tnonetwork\.com|tnonetwork\.com)/i).map((part, i) => {
                if (/tnonetwork\.com/i.test(part)) {
                  return (
                    <Link
                      key={part}
                      href="https://www.tnonetwork.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      color="primary"
                      sx={{ fontWeight: 700 }}
                    >
                      {part}
                    </Link>
                  );
                }
                return part;
              })}
            </>
          ) : (
            <>
              {description}{' '}
              <Link
                href="https://www.tnonetwork.com"
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                sx={{ fontWeight: 700 }}
              >
                tnonetwork.com
              </Link>
              .
            </>
          )}
        </Typography>
      </Box>

      <Grid container spacing={2.5}>
        {BENEFIT_ITEMS.map((item) => (
          <Grid item xs={12} sm={6} key={item.title}>
            <BenefitCard item={item} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

Benefits.propTypes = {
  content: PropTypes.shape({
    overline: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
  }),
  themeMode: PropTypes.string,
};

Benefits.defaultProps = {
  content: {},
  themeMode: 'light',
};

export default Benefits;
